import { useMemo, useState } from "react";
import { SortIcon, Spinner } from "../transacciones/TransactionComponents";
import { sortConsumos } from "./DataColumns";

const SORT_OPTIONS = [
  { key: "fecha", label: "Fecha" },
  { key: "consumoId", label: "ID" },
  { key: "afiliadoId", label: "Asociado" },
  { key: "tipoConsumoId", label: "Tipo" },
  { key: "cantidadSolicitada", label: "Solicitada" },
  { key: "cantidadAutorizada", label: "Autorizada" },
  { key: "copago", label: "Copago" },
];

const TIPO_LABELS = { 1: "Farmacia", 2: "Ambulatorio" };
const MONEDA_LABELS = { 1: "ARS" };

const valueOrDash = (value) => value ?? "-";

const formatDate = (value) => {
  if (!value) return "-";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  return year && month && day ? `${day}/${month}/${year}` : "-";
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const getTipoLabel = (tipo) => TIPO_LABELS[tipo] || `Tipo ${valueOrDash(tipo)}`;

function ConsumoBadge({ estado }) {
  const consumido = String(estado).toLowerCase() === "consumido";
  return (
    <span className={`consumo-status ${consumido ? "consumo-status--consumido" : "consumo-status--liquidado"}`}>
      <span className="consumo-status-dot" />
      {estado || "-"}
    </span>
  );
}

export default function ConsumosResults({
  data = [],
  estado,
  loading,
  pagination,
  onPageChange,
  onPageSizeChange,
}) {
  const [sortConfig, setSortConfig] = useState({ key: "fecha", dir: "desc" });
  const [expandedRows, setExpandedRows] = useState({});
  const sorted = useMemo(
    () => sortConsumos(data, sortConfig.key, sortConfig.dir),
    [data, sortConfig],
  );

  const toggleSort = (key) => {
    setSortConfig((current) => current.key === key
      ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
      : { key, dir: "asc" });
  };

  const toggleExpanded = (id) => {
    setExpandedRows((current) => ({ ...current, [id]: !current[id] }));
  };

  const currentPage = pagination.consultaActual + 1;
  const hasPrevious = pagination.consultaActual > 0;
  const hasNext = data.length >= pagination.cantidad;

  return (
    <div className="consumos-results-card">
      <div className="tx-results-section">
        <div className="tx-results">
          {!loading && data.length > 0 && (
            <div className="tx-results-meta">
              <span className="tx-results-count">
                {`${data.length} resultado${data.length !== 1 ? "s" : ""}`}
              </span>
              <span className="tx-results-badge">Resultados</span>
            </div>
          )}

          {loading ? (
            <Spinner />
          ) : (
            <>
              <div className="tx-list-tools">
                <span className="tx-list-tools-label">Ordenar por</span>
                <div className="tx-sort-controls">
                  {SORT_OPTIONS.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      className={`tx-sort-chip${sortConfig.key === option.key ? " active" : ""}`}
                      onClick={() => toggleSort(option.key)}
                    >
                      {option.label}
                      <SortIcon direction={sortConfig.key === option.key ? sortConfig.dir : null} />
                    </button>
                  ))}
                </div>
              </div>

              <div className="tx-list" role="list">
                {sorted.length === 0 ? (
                  <div className="tx-table-empty">
                    <div className="tx-table-empty-inner">
                      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
                        <circle cx="16" cy="16" r="11" stroke="#D1C4BC" strokeWidth="2" />
                        <path d="M24 24L31 31" stroke="#D1C4BC" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      <span className="tx-table-empty-text">No hay resultados para esta búsqueda</span>
                    </div>
                  </div>
                ) : sorted.map((row) => {
                  const expanded = Boolean(expandedRows[row.consumoId]);
                  return (
                    <article
                      key={row.consumoId}
                      className={`tx-result-row${expanded ? " expanded" : ""}`}
                      role="listitem"
                    >
                      <button
                        type="button"
                        className="tx-result-main consumo-result-main"
                        onClick={() => toggleExpanded(row.consumoId)}
                        aria-expanded={expanded}
                      >
                        <span className="tx-row-chevron" aria-hidden="true">
                          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M5 7L9 11L13 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                        <span className="tx-row-id">#{row.consumoId}</span>
                        <span className="tx-row-content">
                          <span className="tx-row-title">{getTipoLabel(row.tipoConsumoId)}</span>
                          <span className="tx-row-subtitle">
                            {formatDate(row.fecha)} - Asociado {valueOrDash(row.afiliadoId)}
                          </span>
                        </span>
                        <span className="tx-row-metrics">
                          <span className="tx-row-metric">
                            <span className="tx-row-metric-label">Solic.</span>
                            <span className="tx-row-metric-value">{row.cantidadSolicitada ?? 0}</span>
                          </span>
                          <span className="tx-row-metric">
                            <span className="tx-row-metric-label">Aut.</span>
                            <span className="tx-row-metric-value">{row.cantidadAutorizada ?? 0}</span>
                          </span>
                        </span>
                        <span className="tx-row-status"><ConsumoBadge estado={estado} /></span>
                      </button>

                      {expanded && (
                        <div className="tx-result-details">
                          <div className="tx-detail-grid consumo-detail-grid">
                            <div className="tx-detail-item">
                              <span className="tx-detail-label">Transacción origen</span>
                              <span className="tx-detail-value">{valueOrDash(row.transaccionOrigenId)}</span>
                            </div>
                            <div className="tx-detail-item">
                              <span className="tx-detail-label">Prestador</span>
                              <span className="tx-detail-value">{valueOrDash(row.prestadorId)}</span>
                            </div>
                            <div className="tx-detail-item">
                              <span className="tx-detail-label">Copago</span>
                              <span className="tx-detail-value">{formatCurrency(row.copago)}</span>
                            </div>
                            <div className="tx-detail-item">
                              <span className="tx-detail-label">Moneda</span>
                              <span className="tx-detail-value">{MONEDA_LABELS[row.tipoMonedaId] || valueOrDash(row.tipoMonedaId)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>

              <div className="tx-pagination consumo-pagination">
                <label className="consumo-page-size">
                  <span>Items por página</span>
                  <select
                    className="tx-select"
                    value={pagination.cantidad}
                    onChange={(event) => onPageSizeChange(Number(event.target.value))}
                  >
                    {[10, 20, 30, 40, 50].map((size) => <option key={size} value={size}>{size}</option>)}
                  </select>
                </label>
                <div className="tx-pagination-buttons">
                  <button
                    type="button"
                    className="tx-page-btn"
                    onClick={() => onPageChange(pagination.consultaActual - 1)}
                    disabled={!hasPrevious}
                    aria-label="Página anterior"
                  >‹</button>
                  <span className="consumo-page-number">Página {currentPage}</span>
                  <button
                    type="button"
                    className="tx-page-btn"
                    onClick={() => onPageChange(pagination.consultaActual + 1)}
                    disabled={!hasNext}
                    aria-label="Página siguiente"
                  >›</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
